import { EventRecord, ListEventOptions } from '@shared/core/events'
import { useSnackbar } from 'notistack'
import { useMutation, useQueryClient } from 'react-query'
import { useIntl } from 'react-intl'
import { changeArrayElementValues } from 'src/modules/core/utils/helpers'
import { GET_EVENT_BY_ID_CACHE_KEY } from 'src/modules/event/hooks/use-event-by-id'
import { GET_EVENT_LIST_CACHE_KEY } from 'src/modules/event/hooks/use-event-list'
import { STANDING_CONSENT_ACCEPTANCE_HINTS_KEY } from 'src/modules/event/hooks/use-standing-consent-acceptance-hints'
import { GET_USER_EVENT_STATS_CACHE_KEY } from 'src/modules/event/hooks/use-user-event-stats'
import type { EventPreview } from 'src/modules/event/types/event.types'
import { LIST_USER_EVENTS_CACHE_KEY } from 'src/modules/user-events/use-list-user-events'
import { paramsWithScheduledReminders } from 'src/modules/user/constants/profile-params'
import { GET_PROFILE_CACHE_KEY } from 'src/modules/user/hooks/use-profile'
import { ApiService } from 'src/services/api.service'

export const UPDATE_EVENT_CACHE_KEY = 'update-event'

export function useUpdateEvent(requestParams?: ListEventOptions) {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()

  return useMutation(UPDATE_EVENT_CACHE_KEY, (params: Partial<EventRecord>) => ApiService.Events.updateEvent(params), {
    onMutate: async (variables) => {
      await queryClient.cancelQueries([GET_EVENT_LIST_CACHE_KEY, requestParams])

      queryClient.setQueryData(
        [GET_EVENT_LIST_CACHE_KEY, requestParams],
        changeArrayElementValues<EventPreview>(variables.id, (event) => ({
          ...event,
          active: variables?.active ?? event.active,
        })),
      )

      return variables
    },

    onSuccess: async (data, variables) => {
      if (data.auto_activation_revoked) {
        enqueueSnackbar(formatMessage({ id: 'autoActivation.revoked.snackbar' }), { variant: 'info' })
      }

      queryClient.invalidateQueries([GET_EVENT_LIST_CACHE_KEY])
      queryClient.invalidateQueries([GET_EVENT_BY_ID_CACHE_KEY, variables.id])
      queryClient.invalidateQueries([GET_PROFILE_CACHE_KEY, paramsWithScheduledReminders])

      // Refresh the LIST_USER_EVENTS_CACHE_KEY cache key if the event has been activated
      if (variables?.active) {
        queryClient.invalidateQueries([LIST_USER_EVENTS_CACHE_KEY])
        const updatedValue = await ApiService.UserEvents.listUserEvents()
        queryClient.setQueryData([LIST_USER_EVENTS_CACHE_KEY], updatedValue)
      }

      const grantStandingConsentFor = (variables as { grant_standing_consent_for?: string[] })
        .grant_standing_consent_for
      const revokeStandingConsentFor = (variables as { revoke_standing_consent_for?: string[] })
        .revoke_standing_consent_for
      const standingConsentChanged =
        (grantStandingConsentFor?.length ?? 0) > 0 || (revokeStandingConsentFor?.length ?? 0) > 0
      if (standingConsentChanged) {
        queryClient.invalidateQueries(GET_USER_EVENT_STATS_CACHE_KEY)
        queryClient.invalidateQueries(STANDING_CONSENT_ACCEPTANCE_HINTS_KEY)
      }
    },
  })
}
